import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { BadgePercent, Users, ChevronDown, ExternalLink, Loader2, Search } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { asList } from '../../lib/chart-helpers.js'
import { usePersistedState } from '../../lib/persist.js'
import { nameInitial } from '../../lib/text.js'
import { useTypeahead } from '../../lib/useTypeahead.js'
import { Card, DateRange, Segmented, Notice, fmt, toISO, parseISO, todayISO, INPUT } from '../statistic/statShared.jsx'
import { dateParts } from '../../kit/format.js'

/* -------------------------------------------------------------------------- */
/* อัพเซล (upsale) — commission tracking for sold upsale promotions.           */
/*                                                                            */
/* Source: GET /api/upsale?type=default (Xano api:9prbBxvZ) — promotion_patient */
/* sales of commission-bearing promotions, with patient / promotion (Name +   */
/* commission_price) / employee (seller) addons + paging. Sales in a range are */
/* a bounded set, so we fetch ALL pages and aggregate client-side by employee  */
/* or by promotion — count + commission — then visualize + drill down to the   */
/* individual sales. Filter by branch (server) + seller (client). Read-only.   */
/* -------------------------------------------------------------------------- */

const VIOLET = '#7c3aed' // commission bars / headline
const PER_PAGE = 100
const MAX_PAGES = 40

const num = (v) => { const n = Number(String(v ?? '').replace(/[, ฿]/g, '')); return isNaN(n) ? 0 : n }
const addDay = (iso) => { const d = parseISO(iso); return toISO(new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) }
const bkkMs = (iso) => Date.parse(`${iso}T00:00:00+07:00`) // Bangkok day boundary → epoch ms

function fmtTs(v) {
  if (v == null || v === '') return '—'
  // dateParts reads the day in Bangkok, not the browser's timezone
  const p = dateParts(v)
  if (!p) return String(v).slice(0, 10)
  return `${p.d}/${p.m}/${String(p.y).slice(2)}`
}

// normalize one promotion_patient sale row into a flat shape
function normSale(s) {
  const emp = s.employee || null
  const sellerId = String(s.seller ?? '')
  const empName = String(emp?.name || emp?.nickname || emp?.code || (sellerId ? `พนักงาน #${sellerId}` : 'ไม่ระบุผู้ขาย')).trim()
  return {
    id: s.id,
    ts: s.Creation_Date,
    sellerId,
    empName,
    empSub: emp?.nickname && emp.nickname !== empName ? emp.nickname : (emp?.code || ''),
    commission: num(s.promotion?.commission_price),
    promoId: String(s.promotion_id ?? s.promotion?.id ?? ''),
    promoName: String(s.promotion?.Name || '—').trim(),
    promoMain: s.promotion?.Maintype || '',
    promoSub: s.promotion?.Subtype || '',
    patientName: [s.patient?.name, s.patient?.surname].filter(Boolean).join(' ').trim() || '—',
    hn: s.patient?.HN || '',
    branchId: String(s.patient?.branch_id ?? s.branch_id ?? ''),
    patientId: s.patient?.id ?? s.patient_id ?? null,
  }
}

// pivot sales by employee | promotion → [{ key, label, sub, count, commission, sales }]
function pivotUpsale(sales, dim) {
  const m = new Map()
  for (const s of sales) {
    const key = dim === 'employee' ? s.sellerId : s.promoId
    if (!m.has(key)) {
      m.set(key, {
        key, count: 0, commission: 0, sales: [],
        label: dim === 'employee' ? s.empName : s.promoName,
        sub: dim === 'employee' ? s.empSub : [s.promoMain, s.promoSub].filter(Boolean).join(' · '),
      })
    }
    const g = m.get(key)
    g.count += 1; g.commission += s.commission; g.sales.push(s)
  }
  return [...m.values()].sort((a, b) => b.commission - a.commission || b.count - a.count)
}

// fetch EVERY page of the upsale list for the filter
async function fetchAllUpsale(startMs, endMs, branchId) {
  const all = []
  let pageTotal = 1
  let page = 1
  for (; page <= MAX_PAGES; page++) {
    const r = await apiGet('/api/upsale', { type: 'default', start_date: startMs, end_date: endMs, branch_id: branchId, page, per_page: PER_PAGE })
    const items = asList(r)
    all.push(...items)
    pageTotal = Number(r?.pageTotal || 1)
    if (items.length === 0 || page >= pageTotal) break
  }
  return { items: all.map(normSale), capped: pageTotal > page }
}

/* ── small bits ────────────────────────────────────────────────────────────── */
function Select({ value, onChange, children, className = '' }) {
  return <select value={value} onChange={(e) => onChange(e.target.value)} className={`${INPUT} ${className}`}>{children}</select>
}
function Avatar({ name }) {
  return <span className="w-6 h-6 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-[11px] font-semibold shrink-0">{nameInitial(name)}</span>
}
// Single-select typeahead combobox. options: [{ id, name, sub?, search? }]; '' = allLabel.
function SearchSelect({ value, onChange, options, allLabel = 'ทั้งหมด', placeholder = 'ค้นหา…', className = '' }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const inputRef = useRef(null)
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus() }, [open])
  const selected = options.find((o) => o.id === value)
  const label = value === '' ? allLabel : (selected?.name || value)
  const needle = q.trim().toLowerCase()
  const filtered = needle ? options.filter((o) => (o.search || o.name).toLowerCase().includes(needle)) : options
  const pick = (id) => { onChange(id); setOpen(false); setQ('') }
  // Keyboard nav: the always-present "all" row is a real selectable option at
  // index 0; filtered options follow at index i + 1.
  const ta = useTypeahead({
    count: filtered.length + 1,
    onSelect: (i) => { if (i === 0) pick(''); else pick(filtered[i - 1].id) },
    onClose: () => setOpen(false),
  })
  return (
    <div className={`relative ${className}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className={`${INPUT} inline-flex w-full items-center justify-between gap-2 text-left`}>
        <span className={`truncate ${value === '' ? 'text-slate-500' : 'text-slate-700'}`}>{label}</span>
        <ChevronDown size={14} className="text-slate-400 shrink-0" />
      </button>
      {open && (
        <>
          <button className="fixed inset-0 z-10 cursor-default" onClick={() => setOpen(false)} aria-hidden tabIndex={-1} />
          <div className="absolute z-20 mt-1 w-full min-w-[220px] rounded-xl border border-slate-200 bg-white shadow-lg p-2">
            <div className="flex items-center gap-1.5 mb-1.5 rounded-md border border-slate-200 px-2 focus-within:border-brand-300 focus-within:ring-2 focus-within:ring-brand-100">
              <Search size={13} className="text-slate-400 shrink-0" />
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={ta.onKeyDown} placeholder={placeholder} className="w-full py-1 text-[12.5px] bg-transparent focus:outline-none" />
            </div>
            <div ref={ta.listRef} className="max-h-60 overflow-y-auto -mr-1 pr-1">
              <button onClick={() => pick('')} data-ti-index={0} onMouseEnter={() => ta.setActiveIndex(0)} className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] hover:bg-slate-50 ${0 === ta.activeIndex ? 'bg-slate-100' : ''} ${value === '' ? 'text-brand-600 font-medium' : 'text-slate-600'}`}>{allLabel}</button>
              {filtered.map((o, i) => (
                <button key={o.id} onClick={() => pick(o.id)} data-ti-index={i + 1} onMouseEnter={() => ta.setActiveIndex(i + 1)} className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] hover:bg-slate-50 ${i + 1 === ta.activeIndex ? 'bg-slate-100' : ''} ${o.id === value ? 'text-brand-600 font-medium' : 'text-slate-700'}`}>
                  <Avatar name={o.name} />
                  <span className="truncate">{o.name}</span>
                  {o.sub ? <span className="ml-auto text-[11px] text-slate-400 shrink-0 truncate max-w-[45%]">{o.sub}</span> : null}
                </button>
              ))}
              {!filtered.length && <div className="px-2 py-3 text-center text-[12px] text-slate-400">ไม่พบ</div>}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
function SumTile({ label, value, color, strong }) {
  return (
    <div className="min-w-[96px]">
      <div className="flex items-center gap-1.5 text-[11.5px] text-slate-500">{color && <span className="w-2 h-2 rounded-full" style={{ background: color }} />}{label}</div>
      <div className={`tabular-nums leading-tight ${strong ? 'text-[18px] font-semibold text-slate-900' : 'text-[15px] font-medium text-slate-700'}`}>{value}</div>
    </div>
  )
}

// ranked horizontal commission bars
function CommissionBars({ rows, groupBy }) {
  const max = Math.max(1, ...rows.map((r) => r.commission))
  const top = rows.slice(0, 12)
  return (
    <div className="space-y-2.5">
      {top.map((r) => (
        <div key={r.key} className="flex items-center gap-3">
          <div className="w-44 shrink-0 flex items-center gap-2 min-w-0">
            {groupBy === 'employee' && <Avatar name={r.label} />}
            <span className="truncate text-[12.5px] text-slate-700" title={r.label}>{r.label}</span>
          </div>
          <div className="flex-1 h-6 rounded-md bg-slate-100 overflow-hidden">
            <div className="h-full rounded-md transition-all" style={{ width: `${Math.max(2, (r.commission / max) * 100)}%`, background: VIOLET }} />
          </div>
          <div className="w-24 shrink-0 text-right tabular-nums text-[12.5px] font-semibold text-slate-800">{fmt(r.commission)}</div>
          <div className="w-16 shrink-0 text-right tabular-nums text-[12px] text-slate-400">{fmt(r.count)} ครั้ง</div>
        </div>
      ))}
      {rows.length > top.length && <div className="text-[12px] text-slate-400 pt-1">และอีก {fmt(rows.length - top.length)} รายการด้านล่าง…</div>}
    </div>
  )
}

// drill-down: the individual sales inside a group
function SalesList({ sales, groupBy, onOpenPatient }) {
  const sorted = [...sales].sort((a, b) => b.commission - a.commission)
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-2 overflow-x-auto">
      <table className="w-full text-[12.5px] table-fixed">
        <colgroup><col className="w-[80px]" /><col /><col /><col className="w-[150px]" /><col className="w-[100px]" /><col className="w-[56px]" /></colgroup>
        <thead>
          <tr className="text-[11px] text-slate-400 border-b border-slate-100">
            <th className="text-left font-medium py-1">วันที่</th>
            <th className="text-left font-medium">คนไข้</th>
            <th className="text-left font-medium">โปรโมชั่น</th>
            <th className="text-left font-medium">{groupBy === 'employee' ? 'ผู้ขาย' : 'ผู้ขาย'}</th>
            <th className="text-right font-medium">คอมมิชชั่น</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((s) => (
            <tr key={s.id} className="border-b border-slate-50 last:border-0">
              <td className="py-1 text-slate-500 tabular-nums">{fmtTs(s.ts)}</td>
              <td className="pr-2 truncate">{s.patientName}{s.hn ? <span className="text-slate-400 ml-1.5 text-[11px]">HN {s.hn}</span> : null}</td>
              <td className="pr-2 truncate text-slate-700">{s.promoName}</td>
              <td className="pr-2 truncate text-slate-600">{s.empName}</td>
              <td className="text-right tabular-nums font-medium" style={{ color: VIOLET }}>{fmt(s.commission)}</td>
              <td className="text-right">
                {s.patientId ? (
                  <button onClick={() => onOpenPatient(s.patientId)} title="เปิดหน้าคนไข้" className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-1.5 py-1 text-[11px] text-slate-500 hover:border-brand-300 hover:text-brand-600"><ExternalLink size={12} /></button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
const GROUP_OPTS = [{ v: 'employee', label: 'พนักงาน' }, { v: 'promotion', label: 'โปรโมชั่น' }]
const NOUN = { employee: 'พนักงาน', promotion: 'โปรโมชั่น' }

export default function Upsale() {
  const today = useMemo(() => todayISO(), [])   // Bangkok, not the browser clock
  const monthStart = useMemo(() => { const d = parseISO(today); return toISO(new Date(d.getFullYear(), d.getMonth(), 1)) }, [today])

  const [start, setStart] = usePersistedState('ups:start', monthStart)
  const [end, setEnd] = usePersistedState('ups:end', today)
  const [branchId, setBranchId] = usePersistedState('ups:branch', '')
  const [empSel, setEmpSel] = usePersistedState('ups:emp', '')
  const [groupBy, setGroupBy] = usePersistedState('ups:groupBy', 'employee')
  const branchSel = branchId === '0' ? '' : branchId

  const [branchList, setBranchList] = useState([])
  const [sales, setSales] = useState(null)
  const [st, setSt] = useState({ loading: true, err: '', capped: false })
  const [openGroup, setOpenGroup] = useState(null)

  useEffect(() => {
    cachedGet('/api/branches').then((r) => setBranchList(asList(r).map((b) => ({ id: String(b.id ?? b.branch_id), name: b.name || b.branch_name || `สาขา #${b.id}` })))).catch(() => {})
  }, [])

  const startMs = useMemo(() => bkkMs(start), [start])
  const endMs = useMemo(() => bkkMs(addDay(end)), [end]) // end+1 → include end day

  const reqId = useRef(0)
  useEffect(() => {
    const id = ++reqId.current
    setSt({ loading: true, err: '', capped: false })
    setOpenGroup(null)
    fetchAllUpsale(startMs, endMs, branchSel)
      .then(({ items, capped }) => { if (id === reqId.current) { setSales(items); setSt({ loading: false, err: '', capped }) } })
      .catch((e) => { if (id === reqId.current) { setSales(null); setSt({ loading: false, err: String(e.message || e), capped: false }) } })
  }, [startMs, endMs, branchSel])

  useEffect(() => { setOpenGroup(null) }, [groupBy, empSel])

  // seller filter options = only employees who actually sold in this set
  const empOptions = useMemo(() => {
    if (!sales) return []
    const m = new Map()
    for (const s of sales) if (!m.has(s.sellerId)) m.set(s.sellerId, { id: s.sellerId, name: s.empName, sub: s.empSub, search: [s.empName, s.empSub].filter(Boolean).join(' ').toLowerCase() })
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name, 'th'))
  }, [sales])

  const salesF = useMemo(() => (!sales ? [] : (empSel ? sales.filter((s) => s.sellerId === empSel) : sales)), [sales, empSel])
  const grand = useMemo(() => salesF.reduce((a, s) => ({ count: a.count + 1, commission: a.commission + s.commission }), { count: 0, commission: 0 }), [salesF])
  const nEmp = useMemo(() => new Set(salesF.map((s) => s.sellerId)).size, [salesF])
  const nPromo = useMemo(() => new Set(salesF.map((s) => s.promoId)).size, [salesF])
  const groupRows = useMemo(() => pivotUpsale(salesF, groupBy), [salesF, groupBy])

  const openPatient = (pid) => { if (pid) window.open(`/patient/${pid}`, '_blank', 'noopener') }

  return (
    <div className="px-8 py-6 max-w-[1180px]">
      {/* header */}
      <div className="flex items-center gap-2 text-[13px] text-slate-400 mb-1">
        <Link to="/reports" className="hover:text-brand-600">รายงาน</Link><span>/</span><span className="text-slate-600">อัพเซล</span>
      </div>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0"><BadgePercent size={22} strokeWidth={1.75} /></div>
        <div>
          <h1 className="text-[21px] font-semibold text-slate-900 tracking-tight leading-tight">อัพเซล</h1>
          <p className="text-[13px] text-slate-500">โปรโมชั่นอัพเซลที่ขายได้ · จำนวนและคอมมิชชั่นของพนักงานแต่ละคน · อ่านอย่างเดียว</p>
        </div>
      </div>

      {/* filter bar */}
      <Card icon={<Search size={18} />} title="ค้นหา / กรอง" subtitle="ช่วงวันที่ · สาขา · พนักงานผู้ขาย · จัดกลุ่มตาม">
        <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">ช่วงวันที่</label>
            <DateRange start={start} end={end} today={today} onStart={setStart} onEnd={setEnd} />
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">สาขา</label>
            <Select value={branchSel} onChange={setBranchId} className="min-w-[140px]">
              <option value="">ทุกสาขา</option>
              {branchList.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Select>
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">พนักงานผู้ขาย <span className="text-slate-300">({empOptions.length})</span></label>
            <SearchSelect value={empSel} onChange={setEmpSel} options={empOptions} allLabel="ทุกคน" placeholder="ค้นหาชื่อพนักงาน…" className="w-[200px]" />
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">จัดกลุ่มตาม</label>
            <Segmented value={groupBy} onChange={setGroupBy} options={GROUP_OPTS} />
          </div>
        </div>
      </Card>

      {st.err ? (
        <div className="mt-4"><Notice tone="error" text={`โหลดข้อมูลไม่สำเร็จ: ${st.err}`} /></div>
      ) : st.loading ? (
        <div className="mt-4"><Card icon={<BadgePercent size={18} />} title="กำลังโหลด…"><div className="flex items-center justify-center gap-2 text-[13px] text-slate-400 h-[200px]"><Loader2 size={16} className="animate-spin" /> กำลังโหลดยอดอัพเซล…</div></Card></div>
      ) : (
        <>
          {st.capped && <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-[12.5px] text-amber-700">พบข้อมูลจำนวนมาก — แสดงเฉพาะ {fmt(MAX_PAGES * PER_PAGE)} รายการแรก กรุณาย่อช่วงวันที่เพื่อดูให้ครบ</div>}

          {/* summary */}
          <div className="mt-4">
            <Card icon={<BadgePercent size={18} />} title="สรุปคอมมิชชั่นอัพเซล" subtitle="คอมมิชชั่นรวม = ผลรวม commission_price ของโปรโมชั่นที่ขายได้ในช่วง">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                <SumTile label="คอมมิชชั่นรวม" value={fmt(grand.commission)} color={VIOLET} strong />
                <SumTile label="จำนวนที่ขายได้" value={`${fmt(grand.count)} ครั้ง`} />
                <SumTile label="พนักงานผู้ขาย" value={`${fmt(nEmp)} คน`} />
                <SumTile label="โปรโมชั่น" value={`${fmt(nPromo)} รายการ`} />
              </div>
            </Card>
          </div>

          {!groupRows.length ? (
            <div className="mt-4"><Notice tone="warn" text="ไม่พบยอดอัพเซลในช่วงและตัวกรองที่เลือก" /></div>
          ) : (
            <>
              {/* chart */}
              <div className="mt-4">
                <Card icon={<Users size={18} />} title={`คอมมิชชั่นแยกตาม${NOUN[groupBy]}`} badge={`${fmt(groupRows.length)} ${NOUN[groupBy]}`} subtitle="เรียงตามคอมมิชชั่นมากไปน้อย">
                  <CommissionBars rows={groupRows} groupBy={groupBy} />
                </Card>
              </div>

              {/* grouped table + drill-down */}
              <div className="mt-4">
                <Card icon={<BadgePercent size={18} />} title={`รายละเอียดแยกตาม${NOUN[groupBy]}`} subtitle="กดที่แถวเพื่อดูรายการที่ขายได้">
                  <div className="overflow-x-auto -mx-1 px-1">
                    <table className="w-full text-[13px] table-fixed">
                      <colgroup><col className="w-[34px]" /><col /><col className="w-[120px]" /><col className="w-[140px]" /></colgroup>
                      <thead>
                        <tr className="text-[11.5px] text-slate-400 border-b border-slate-200">
                          <th></th>
                          <th className="text-left font-medium py-2">{NOUN[groupBy]}</th>
                          <th className="text-right font-medium">จำนวน (ครั้ง)</th>
                          <th className="text-right font-medium">คอมมิชชั่น</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupRows.map((row) => {
                          const open = openGroup === row.key
                          return (
                            <Fragment key={row.key}>
                              <tr className={`border-b border-slate-100 cursor-pointer ${open ? 'bg-brand-50/40' : 'hover:bg-slate-50/70'}`} onClick={() => setOpenGroup(open ? null : row.key)}>
                                <td className="py-2.5 text-slate-300"><ChevronDown size={16} className={`transition ${open ? '' : '-rotate-90'}`} /></td>
                                <td className="pr-2">
                                  <span className="inline-flex items-center gap-2 min-w-0">
                                    {groupBy === 'employee' && <Avatar name={row.label} />}
                                    <span className="truncate font-medium text-slate-800">{row.label}</span>
                                    {row.sub ? <span className="text-[11px] text-slate-400 shrink-0 truncate">{row.sub}</span> : null}
                                  </span>
                                </td>
                                <td className="text-right tabular-nums text-slate-700">{fmt(row.count)}</td>
                                <td className="text-right tabular-nums font-semibold" style={{ color: VIOLET }}>{fmt(row.commission)}</td>
                              </tr>
                              {open && (
                                <tr>
                                  <td></td>
                                  <td colSpan={3} className="py-2 pr-1"><SalesList sales={row.sales} groupBy={groupBy} onOpenPatient={openPatient} /></td>
                                </tr>
                              )}
                            </Fragment>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
